/**
 * The one fetch site. Nothing in `src/features/**` calls `fetch` directly.
 *
 * This module is deliberately auth-unaware: it sends a request, unwraps the
 * envelope, and records the edge's `X-Request-ID`. The session, the silent
 * refresh, and the retry policy live one layer up in `client.ts`, so the
 * refresh call itself can use this module without recursing through the very
 * 401 handling it exists to serve.
 */
import { apiUrl, type ServiceKey } from './config';
import { ApiError, normaliseRequestId, parseEnvelope } from './envelope';

export type HttpMethod = 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';

export interface HttpRequest {
  method: HttpMethod;
  /** Path under `/<service>/api/v1`, e.g. `/me/subscription`. */
  path: string;
  /**
   * The backend that owns this path, and the base URL the request is sent to.
   *
   * Optional because the routing table in `@shared/api/config` already answers
   * it for every path this repo calls. It is here for a caller whose path the
   * table cannot claim from its shape alone, and it is what makes the service an
   * input to this layer rather than a value the layer can only infer.
   */
  service?: ServiceKey;
  body?: unknown;
  /**
   * An ARRAY value becomes a REPEATED parameter, never a comma-joined string.
   * `backend-analytics` compiles `editorialPostIds` to `= ANY($1)`, and a
   * joined value reaches it as one malformed ULID — a `422` on a read whose
   * only job is to decorate a card with a figure.
   */
  searchParams?: Record<string, string | number | boolean | readonly string[] | undefined>;
  headers?: Record<string, string>;
  signal?: AbortSignal;
}

/**
 * The last `X-Request-ID` the edge returned. Error surfaces render it as a
 * copyable chip and the telemetry payload carries it, so one browser failure
 * can be joined to the backend log line that caused it. It is never generated
 * here — nginx is the only source.
 */
let lastRequestId: string | null = null;

export function readLastRequestId(): string | null {
  return lastRequestId;
}

function buildUrl(path: string, searchParams?: HttpRequest['searchParams'], service?: ServiceKey): string {
  const url = new URL(apiUrl(path, service));
  if (searchParams) {
    for (const [key, value] of Object.entries(searchParams)) {
      if (value === undefined) continue;
      // `append` per element, so `?ids=A&ids=B`. `set` would keep the last one
      // and silently ask about a single subject.
      if (Array.isArray(value)) for (const item of value) url.searchParams.append(key, String(item));
      else url.searchParams.set(key, String(value));
    }
  }
  return url.toString();
}

/**
 * A multipart body is passed through UNTOUCHED and carries no `Content-Type`
 * of ours: the boundary token is generated by the browser when it serialises
 * the `FormData`, and a hand-written header would name a boundary that does
 * not exist in the body. The avatar upload of `POST /me/avatar` is the one
 * caller — every other write on this repo is JSON.
 */
function isMultipart(body: unknown): body is FormData {
  return typeof FormData !== 'undefined' && body instanceof FormData;
}

/** One request, one envelope unwrap. No retries, no refresh, no session. */
export async function rawRequest<T>(request: HttpRequest): Promise<T> {
  let response: Response;
  const multipart = isMultipart(request.body);

  // Built OUTSIDE the try, and that placement is the point. An unrouted path and
  // a missing base URL both throw here, and both are programming or deployment
  // faults rather than network conditions. Inside the try they would be caught
  // and re-thrown as `CLIENT_NETWORK_UNREACHABLE`, which tells the reader the
  // service is down and tells the developer nothing at all.
  const url = buildUrl(request.path, request.searchParams, request.service);

  try {
    response = await fetch(url, {
      method: request.method,
      // The refresh cookie is httpOnly and host-only; it rides along on the
      // refresh call because credentials are sent, not because we read it.
      credentials: 'include',
      headers: {
        Accept: 'application/json',
        ...(request.body === undefined || multipart
          ? {}
          : { 'Content-Type': 'application/json; charset=utf-8' }),
        ...request.headers,
      },
      body:
        request.body === undefined
          ? undefined
          : multipart
            ? (request.body as FormData)
            : JSON.stringify(request.body),
      signal: request.signal,
    });
  } catch (cause) {
    // Offline, DNS, TLS, or an aborted connection — never a backend answer.
    //
    // THE SIGNAL IS THE AUTHORITY, NOT THE THROWN VALUE. An aborted fetch rejects with a
    // `DOMException` named `AbortError`, and `DOMException` does NOT inherit from `Error`
    // in this runtime — `cause instanceof Error` is false for it. Guarding on that
    // instanceof, as this line used to, made the AbortError branch unreachable and
    // reported every reader-initiated cancellation as CLIENT_NETWORK_UNREACHABLE: a
    // superseded search or a navigation away surfaced to the reader as a service outage.
    //
    // `signal.aborted` is the state itself rather than an inference from the error, so it
    // holds regardless of what the runtime chose to throw. The name check stays as the
    // fallback for a cancel that reaches here without the caller's own signal, and reads
    // the property without an instanceof so a DOMException still satisfies it.
    const aborted =
      request.signal?.aborted === true ||
      (typeof cause === 'object' &&
        cause !== null &&
        (cause as { readonly name?: unknown }).name === 'AbortError');
    throw new ApiError({
      status: 0,
      errorCode: aborted ? 'CLIENT_ABORTED' : 'CLIENT_NETWORK_UNREACHABLE',
      message: 'تعذّر الوصول إلى الخدمة.',
      xRequestId: lastRequestId,
    });
  }

  lastRequestId = normaliseRequestId(response.headers.get('X-Request-ID')) ?? lastRequestId;

  return parseEnvelope<T>(response);
}
